import { memo } from "react";
import {
  selectCurrentPage,
  selectProductPageAmount,
  selectVisibleTabularViewColumns,
} from "~/features/search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { changePage, changeSort } from "~/features/search/redux/actions";
import { ReduxProductTabularView } from "~/features/search/components/ProductResults/views/TabularView/ReduxProductTabularView";
import Pagination from "~/features/search/components/common/Pagination";
import { ReduxImperialToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { saActiveTab } from "~/features/selectionAssistant/bearing/redux/selector";
import { SaTabs } from "~/features/selectionAssistant/bearing/redux/types";
import { useTranslation } from "react-i18next";
import { ResetSortBtn } from "~/features/selectionAssistant/bearing/components/Buttons/ResetSortBtn";
import { styled } from "~/common/mui";
import { OpenAbbreviationsBtn } from "~/features/selectionAssistant/bearing/components/AbbreviationListModal/AbbreviationListModal";
import { BSA_ATTRIBUTES } from "~/features/selectionAssistant/bearing/components/AbbreviationListModal/constants";
import { Alert } from "~/common/mui/Alert/Alert";
import { C_ON_PRIMARY, C_PRIMARY } from "~/common/components/icon/Colors";

const ResultsTitle = styled("h2")(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: `0 0 ${theme.spacing(2)}`,
  [theme.breakpoints.up("desktop-s")]: {
    ...theme.typography.headlineSmall,
    marginBottom: theme.spacing(3.75),
  },
}));

const SAPagination = styled(Pagination)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    margin: `${theme.spacing(2.5)} 0`,
  },
  "& .MuiPaginationItem-page": {
    borderRadius: "50%",
    "&.Mui-selected": {
      color: C_ON_PRIMARY,
      backgroundColor: C_PRIMARY,
    },
  },
}));

const ButtonList = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flexDirection: "column",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(2),
  },
}));

interface Props {
  totalNumberOfResults: number;
  isDesktop?: boolean;
}

function BSAResults() {
  const dispatch = useDispatch();
  const pageAmount = useSelector(selectProductPageAmount);
  const currentPage = useSelector(selectCurrentPage);
  const onPageChanged = (newPageNumber: number) => dispatch(changePage(newPageNumber));

  return (
    <>
      <ReduxProductTabularView maxColumnAmount={99} isSA={true} />
      {pageAmount > 1 && (
        <SAPagination
          pageAmount={pageAmount}
          currentPage={currentPage}
          onPageChange={onPageChanged}
          goToIdAfterChange={"#results"}
          renderHrefs={true}
        />
      )}
    </>
  );
}

export const SATabularView = memo(({ totalNumberOfResults, isDesktop = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useSelector(selectVisibleTabularViewColumns);
  const activeTab = useSelector(saActiveTab);

  if (!config) {
    return null;
  }

  return (
    <div className={"d-flex flex-direction-column"}>
      <ResultsTitle data-test-id={"selectionassistant-resultstitle"}>
        {t("page.selectionAssistant.productsHeader", {
          count: totalNumberOfResults,
        })}
      </ResultsTitle>
      {isDesktop && activeTab !== SaTabs.DIMENSIONS && <ReduxImperialToggle className={"mb-30"} page={"SA"} />}

      <ButtonList>
        <ResetSortBtn onClick={() => dispatch(changeSort("name"))} />

        {!isDesktop && <OpenAbbreviationsBtn attributes={BSA_ATTRIBUTES} />}
      </ButtonList>

      <BSAResults key={config.length} />

      <Alert severity={"info"} className={"mt-2"}>
        {t("page.selectionAssistant.units.blueMessage")}
      </Alert>
    </div>
  );
});
